import "@dxos/node-std/globals"

// packages/common/keys/src/public-key.ts
import assert from "@dxos/node-std/assert";
import { inspect } from "@dxos/node-std/util";
import randomBytes from "randombytes";
import { truncateKey, devtoolsFormatter, equalsSymbol } from "@dxos/debug";
var PUBLIC_KEY_LENGTH = 32;
var SECRET_KEY_LENGTH = 64;
var PublicKey = class {
  /**
  * Creates new instance of PublicKey automatically determining the input format.
  * @param source A Buffer, or Uint8Array, or hex encoded string, or something with an `asUint8Array` method on it
  * @returns PublicKey
  */
  static from(source) {
    assert(source);
    if (source instanceof PublicKey) {
      return source;
    } else if (source instanceof Buffer) {
      return new PublicKey(new Uint8Array(source));
    } else if (source instanceof Uint8Array) {
      return new PublicKey(source);
    } else if (source instanceof ArrayBuffer) {
      return new PublicKey(new Uint8Array(source));
    } else if (typeof source === "string") {
      return PublicKey.fromHex(source);
    } else if (source.asUint8Array) {
      return new PublicKey(source.asUint8Array());
    } else {
      throw new TypeError(`Unable to create PublicKey from ${source}`);
    }
  }
  /**
  * Same as `PublicKey.from` but does not throw and instead returns a `{ key: PublicKey }` or `{ error: Error }`
  * @param source Same PublicKeyLike argument as for `PublicKey.from`
  * @returns PublicKey
  */
  static safeFrom(source) {
    if (!source) {
      return void 0;
    }
    try {
      return PublicKey.from(source);
    } catch (error) {
      return void 0;
    }
  }
  /**
  * Creates new instance of PublicKey from hex string.
  */
  static fromHex(hex) {
    if (hex.startsWith("0x")) {
      hex = hex.slice(2);
    }
    return new PublicKey(new Uint8Array(Buffer.from(hex, "hex")));
  }
  /**
  * Creates a new key.
  */
  static random() {
    return PublicKey.from(randomBytes(32));
  }
  static *randomSequence() {
    for (let i = 0; i < 1e4; i++) {
      yield PublicKey.random();
    }
    throw new Error("Too many keys requested");
  }
  /**
  * Tests if provided values is an instance of PublicKey.
  */
  static isPublicKey(value) {
    return value instanceof PublicKey;
  }
  /**
  * Asserts that provided values is an instance of PublicKey.
  */
  static assertValidPublicKey(value) {
    if (!this.isPublicKey(value)) {
      throw new TypeError("Invalid PublicKey");
    }
  }
  /**
  * Tests two keys for equality.
  */
  static equals(left, right) {
    return PublicKey.from(left).equals(right);
  }
  /**
  * @param Hex string representation of key.
  * @return Key buffer.
  * @deprecated All keys should be represented as instances of PublicKey.
  */
  static bufferize(str) {
    assert(typeof str === "string", "Invalid type");
    const buffer = Buffer.from(str, "hex");
    return buffer;
  }
  /**
  * @param Public key like data structure (but not PublicKey which should use toString).
  * @return Hex string representation of key.
  * @deprecated All keys should be represented as instances of PublicKey.
  */
  static stringify(key) {
    if (key instanceof PublicKey) {
      key = key.asBuffer();
    } else if (key instanceof Uint8Array) {
      key = Buffer.from(key);
    }
    assert(key instanceof Buffer, "Invalid type");
    return key.toString("hex");
  }
  /**
  * To be used with ComplexMap and ComplexSet.
  * Returns a scalar representation for this key.
  */
  static hash(key) {
    return key.toHex();
  }
  constructor(_value) {
    this._value = _value;
    if (!(_value instanceof Uint8Array)) {
      throw new TypeError(`Expected Uint8Array, got: ${_value}`);
    }
  }
  truncate(length = 8) {
    return truncateKey(this, {
      length
    });
  }
  toString() {
    return this.toHex();
  }
  // TODO(burdon): How is this used?
  toJSON() {
    return this.toHex();
  }
  toHex() {
    return this.asBuffer().toString("hex");
  }
  asBuffer() {
    return Buffer.from(this._value);
  }
  asUint8Array() {
    return this._value;
  }
  getInsecureHash(modulo) {
    return Math.abs(this._value.reduce((acc, val) => acc ^ val | 0, 0)) % modulo;
  }
  /**
  * Used by Node.js to get textual representation of this object when it's printed with a `console.log` statement.
  */
  [inspect.custom](depth, options) {
    if (!options.colors || typeof process.stdout.hasColors !== "function" || !process.stdout.hasColors()) {
      return `<PublicKey ${this.truncate()}>`;
    }
    const printControlCode = (code) => {
      return `\x1B[${code}m`;
    };
    const colors = [
      "red",
      "green",
      "yellow",
      "blue",
      "magenta",
      "cyan",
      "redBright",
      "greenBright",
      "yellowBright",
      "blueBright",
      "magentaBright",
      "cyanBright",
      "whiteBright"
    ];
    const color = colors[this.getInsecureHash(colors.length)];
    return `PublicKey(${printControlCode(inspect.colors[color][0])}${this.truncate()}${printControlCode(inspect.colors.reset[0])})`;
  }
  get [devtoolsFormatter]() {
    return {
      header: () => {
        const colors = [
          "darkred",
          "green",
          "orange",
          "blue",
          "darkmagenta",
          "darkcyan",
          "red",
          "green",
          "orange",
          "blue",
          "magenta",
          "darkcyan",
          "black"
        ];
        const color = colors[this.getInsecureHash(colors.length)];
        return [
          "span",
          {},
          [
            "span",
            {},
            "PublicKey("
          ],
          [
            "span",
            {
              style: `color: ${color};`
            },
            this.truncate()
          ],
          [
            "span",
            {},
            ")"
          ]
        ];
      }
    };
  }
  /**
  * Test this key for equality with some other key.
  */
  equals(other) {
    const otherConverted = PublicKey.from(other);
    if (this._value.length !== otherConverted._value.length) {
      return false;
    }
    let equal = true;
    for (let i = 0; i < this._value.length; i++) {
      equal && (equal = this._value[i] === otherConverted._value[i]);
    }
    return equal;
  }
  [equalsSymbol](other) {
    if (!PublicKey.isPublicKey(other)) {
      return false;
    }
    return this.equals(other);
  }
};
export {
  PUBLIC_KEY_LENGTH,
  PublicKey,
  SECRET_KEY_LENGTH
};
//# sourceMappingURL=index.mjs.map
